import { Injectable } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { DemoFlow } from './demo.constants';

/**
 * Lightweight registry that lets a page expose itself to the demo player while
 * demo mode is active. A page registers its reactive `form` (so the player can
 * drive values straight through the FormControls — the reliable way to feed
 * PrimeNG inputs) plus optional imperative `actions` for interactions that
 * can't be expressed as a plain control write (e.g. the invoice line's
 * `ngModel`-based product picker, or kicking off a save with custom routing).
 *
 * Registration is a no-op cost when demo mode is off because pages only call it
 * behind a `demoMode.active()` guard.
 */
export interface DemoStageRegistration {
  form?: AbstractControl;
  actions?: Record<string, (...args: unknown[]) => void>;
}

@Injectable({ providedIn: 'root' })
export class DemoStageService {
  private readonly registrations = new Map<DemoFlow, DemoStageRegistration>();

  register(flow: DemoFlow, registration: DemoStageRegistration): void {
    this.registrations.set(flow, registration);
  }

  unregister(flow: DemoFlow): void {
    this.registrations.delete(flow);
  }

  form(flow: DemoFlow): AbstractControl | undefined {
    return this.registrations.get(flow)?.form;
  }

  control(flow: DemoFlow, path: string): AbstractControl | null {
    return this.registrations.get(flow)?.form?.get(path) ?? null;
  }

  runAction(flow: DemoFlow, name: string, args: unknown[]): void {
    this.registrations.get(flow)?.actions?.[name]?.(...args);
  }

  hasFlow(flow: DemoFlow): boolean {
    return this.registrations.has(flow);
  }
}
